import { IJwtClaims, readJwtClaims } from './jwtClaims';
import { IPublicTenantContext } from './models/publicTenantContext';
import { IBranding } from './models/tenantBranding';

/**
 * The clinic's branding, remembered between page loads.
 *
 * This exists for one frame of one moment: a reload while signed in. Without it the app
 * boots on Carefull's palette, paints, and then repaints in the clinic's colours a round
 * trip later -- a visible flash of somebody else's brand on every refresh. Seeding the
 * provider's initial state from here happens synchronously, before first paint, so the
 * clinic's colours are on screen from the first frame and the network response usually
 * changes nothing.
 *
 * **Keyed by tenant, always.** A person can belong to more than one clinic, and an unkeyed
 * cache would show them the last one they visited while they are signed in to a different
 * one -- a worse failure than the flash this is here to prevent.
 *
 * The logo URL is cached but the image is not: the browser's own HTTP cache holds that, and
 * the endpoint sends an ETag so a repeat load is a 304.
 */

const KEY_PREFIX = 'brandingCache:';

/**
 * The clinic a HOSTNAME belongs to, remembered separately from the one a SESSION belongs to.
 *
 * Same job as the tenant-keyed cache above -- no flash of somebody else's brand on the first
 * frame -- for the case that cache cannot cover: a signed-out visitor. There is no token to
 * read a tenant id out of, so the key is the hostname, which is known before any JavaScript
 * runs a request.
 */
const HOST_KEY_PREFIX = 'brandingCache:host:';

/**
 * The clinic a given session is for, from the JWT's `tid` claim.
 *
 * Deliberately total: any malformed token is "no tenant", which costs a cache miss and never
 * an exception during render.
 *
 * A token with no `tid` is a platform session (SUPER_ADMIN), which belongs to no clinic and
 * so has no branding to remember.
 *
 * Takes the token rather than reading it, because `BrandingProvider` has a fresher one than
 * localStorage during a sign-in and needs the answer for that token, not the stored one.
 */
export const tenantIdFromToken = (token: string | null): number | null => {
    const claims: IJwtClaims | null = readJwtClaims(token);

    if (!claims) {
        return null;
    }

    // An expired token names no clinic, because it names no session.
    //
    // This has to agree with `apiService.isAuthenticated()`, which does check `exp`. When
    // it did not, the two readers of one token disagreed: AuthContext treated the person as
    // signed out while BrandingProvider still saw a tenant, fired `GET /tenant-branding`
    // with the dead token, and got a rejection back -- once per render, indefinitely.
    //
    // Returning null here also lets the hostname take over, which is the right answer
    // anyway: on a clinic's own domain an expired session should leave the visitor looking
    // at that clinic's front door, not at a failing request.
    if (typeof claims.exp === 'number' && claims.exp * 1000 <= Date.now()) {
        return null;
    }

    return typeof claims.tid === 'number' ? claims.tid : null;
};

/** The clinic the *stored* session is for -- {@link tenantIdFromToken} of what is on disk. */
export const currentTenantId = (): number | null =>
    tenantIdFromToken(localStorage.getItem('authToken'));

export const readCachedBranding = (tenantId: number | null): IBranding | null => {
    if (tenantId === null) {
        return null;
    }

    try {
        const stored = localStorage.getItem(`${KEY_PREFIX}${tenantId}`);

        return stored ? (JSON.parse(stored) as IBranding) : null;
    } catch {
        return null;
    }
};

export const writeCachedBranding = (tenantId: number | null, branding: IBranding): void => {
    if (tenantId === null) {
        return;
    }

    try {
        localStorage.setItem(`${KEY_PREFIX}${tenantId}`, JSON.stringify(branding));
    } catch {
        // A full or disabled localStorage costs the flash, nothing else. Not worth
        // failing a render over.
    }
};

export const readCachedHostContext = (hostname: string): IPublicTenantContext | null => {
    try {
        const stored = localStorage.getItem(`${HOST_KEY_PREFIX}${hostname}`);

        return stored ? (JSON.parse(stored) as IPublicTenantContext) : null;
    } catch {
        return null;
    }
};

export const writeCachedHostContext = (
    hostname: string,
    context: IPublicTenantContext
): void => {
    try {
        localStorage.setItem(`${HOST_KEY_PREFIX}${hostname}`, JSON.stringify(context));
    } catch {
        // A full or disabled localStorage costs the flash, nothing else.
    }
};

/**
 * Forgets every clinic's SESSION branding, and deliberately keeps the host's.
 *
 * Every clinic's rather than the current one's, because this runs where a session is being
 * torn down -- and by then the token that would name the tenant may already be gone.
 * Called from both places that clear auth: `authService.removeAuthToken`, and
 * `services/sessionKeeper.ts` when the server refuses the stored token.
 *
 * **The host-keyed entry survives, and that is the point.** Signing out of Motion Care on
 * motioncare.ro leaves you standing on Motion Care's front door: the clinic did not change,
 * only the session did. Clearing it here would repaint the page in the platform's colours at
 * the moment of signing out -- which is precisely the "flash of somebody else's brand" this
 * whole file exists to prevent, and would tell every patient that the clinic they use is
 * somebody else's product.
 *
 * That is an inversion of what this function used to do, and it is safe for the reason the
 * session cache is not: a hostname is public. It names the clinic to anyone who types it, and
 * nothing about it is scoped to the person who just signed out.
 */
export const clearCachedBranding = (): void => {
    try {
        Object.keys(localStorage)
            .filter((key: string) => key.startsWith(KEY_PREFIX)
                && !key.startsWith(HOST_KEY_PREFIX))
            .forEach((key: string) => localStorage.removeItem(key));
    } catch {
        // As above.
    }
};
